/**
 * useUndoRedo - Hook para acceder al servicio de Undo/Redo
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-tools
 *
 * @description
 * Hook React que proporciona acceso al servicio de undo/redo
 * y se suscribe a cambios en el historial.
 *
 * @example
 * ```tsx
 * import { useUndoRedo } from '@/core/map-tools';
 *
 * function ToolPanel() {
 *   const { canUndo, canRedo, undo, redo } = useUndoRedo();
 *
 *   return (
 *     <div>
 *       <button disabled={!canUndo} onClick={undo}>Deshacer</button>
 *       <button disabled={!canRedo} onClick={redo}>Rehacer</button>
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { undoRedoService } from './UndoRedoService';

/**
 * Estado del historial
 */
export interface UndoRedoState {
  /**
   * Hay operaciones para deshacer
   */
  canUndo: boolean;

  /**
   * Hay operaciones para rehacer
   */
  canRedo: boolean;

  /**
   * Cantidad de operaciones en el historial de undo
   */
  undoCount: number;

  /**
   * Cantidad de operaciones en el historial de redo
   */
  redoCount: number;

  /**
   * Descripción de la próxima operación a deshacer
   */
  undoDescription: string | null;

  /**
   * Descripción de la próxima operación a rehacer
   */
  redoDescription: string | null;
}

/**
 * Acciones del historial
 */
export interface UndoRedoActions {
  /**
   * Deshace la última operación
   */
  undo: () => void;

  /**
   * Rehace la última operación deshecha
   */
  redo: () => void;

  /**
   * Limpia todo el historial
   */
  clear: () => void;
}

/**
 * Retorno del hook
 */
export type UseUndoRedoReturn = UndoRedoState & UndoRedoActions;

/**
 * Hook useUndoRedo
 */
export function useUndoRedo(): UseUndoRedoReturn {
  const [state, setState] = useState<UndoRedoState>(() => {
    const serviceState = undoRedoService.getState();
    return {
      ...serviceState,
      undoDescription: undoRedoService.getUndoDescription(),
      redoDescription: undoRedoService.getRedoDescription(),
    };
  });

  /**
   * Actualiza el estado desde el servicio
   */
  const updateState = useCallback(() => {
    const serviceState = undoRedoService.getState();
    setState({
      ...serviceState,
      undoDescription: undoRedoService.getUndoDescription(),
      redoDescription: undoRedoService.getRedoDescription(),
    });
  }, []);

  /**
   * Se suscribe a cambios en el historial
   */
  useEffect(() => {
    const unsubscribe = undoRedoService.onChange(() => {
      updateState();
    });

    return unsubscribe;
  }, [updateState]);

  /**
   * Handlers estables
   */
  const undo = useCallback(() => {
    undoRedoService.undo();
  }, []);

  const redo = useCallback(() => {
    undoRedoService.redo();
  }, []);

  const clear = useCallback(() => {
    undoRedoService.clear();
  }, []);

  return {
    ...state,
    undo,
    redo,
    clear,
  };
}
